// client/src/writer/components/ModelSelectionDialog.tsx
// Enhanced model selection with error recovery options

import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Bot, Zap, Star, DollarSign, AlertTriangle, CheckCircle, Clock } from 'lucide-react';
import { cn } from '@/lib/utils';

interface ModelSelectionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  taskType: 'blueprint' | 'chapter_outline' | 'chapter_text' | 'story_enhancement';
  onModelSelect: (model: LlmModel) => void;
  baseCost: number;
  failedModel?: string;
  errorMessage?: string;
  userPlan?: string;
  creditBalance?: number;
}

interface LlmModel {
  id: number;
  provider: string;
  model_name: string;
  api_name: string;
  context_window: string;
  input_cost_per_1m_tok: number;
  output_cost_per_1m_tok: number;
  capability_index: number;
  credit_multiplier: number;
  min_plan_required: string;
  is_active: boolean;
  key_notes: string;
  display_name?: string;
}

const PROVIDER_ICONS: Record<string, React.ElementType> = {
  'OpenAI': Star,
  'Google': Zap,
  'DeepSeek': DollarSign,
  'Anthropic': Bot
};

const PROVIDER_COLORS: Record<string, string> = {
  'OpenAI': 'bg-emerald-100 text-emerald-800',
  'Google': 'bg-blue-100 text-blue-800', 
  'DeepSeek': 'bg-purple-100 text-purple-800',
  'Anthropic': 'bg-orange-100 text-orange-800'
};

const PLAN_HIERARCHY = ['FREE', 'PRO', 'ENTERPRISE'];

export function ModelSelectionDialog({
  open,
  onOpenChange,
  taskType,
  onModelSelect,
  baseCost,
  failedModel,
  errorMessage,
  userPlan = 'FREE',
  creditBalance = 0
}: ModelSelectionDialogProps) {
  const [selectedModel, setSelectedModel] = useState<LlmModel | null>(null);

  const { data: modelsData, isLoading } = useQuery<LlmModel[]>({
    queryKey: ['/api/llm-models'],
    enabled: open
  });

  const handleSelectModel = () => {
    if (selectedModel) {
      onModelSelect(selectedModel);
      onOpenChange(false);
      setSelectedModel(null);
    }
  };

  const filterAndSortModels = (models: LlmModel[]) => {
    const userPlanIndex = PLAN_HIERARCHY.indexOf(userPlan);
    
    return models
      .filter(model => model.is_active)
      .filter(model => {
        const modelPlanIndex = PLAN_HIERARCHY.indexOf(model.min_plan_required);
        return modelPlanIndex <= userPlanIndex;
      })
      .sort((a, b) => {
        // Sort by provider first, then by credit cost
        if (a.provider !== b.provider) {
          return a.provider.localeCompare(b.provider);
        }
        return a.credit_multiplier - b.credit_multiplier;
      });
  };

  const groupModelsByProvider = (models: LlmModel[]) => {
    const grouped: Record<string, LlmModel[]> = {};
    models.forEach(model => {
      if (!grouped[model.provider]) {
        grouped[model.provider] = [];
      }
      grouped[model.provider].push(model);
    });
    return grouped;
  };

  const canAfford = (model: LlmModel) => {
    const totalCost = Math.ceil(baseCost * model.credit_multiplier);
    return creditBalance >= totalCost;
  };

  const getModelStatus = (model: LlmModel) => {
    if (model.api_name === failedModel) {
      return { icon: AlertTriangle, color: 'text-red-500', label: 'Failed' };
    }
    if (canAfford(model)) {
      return { icon: CheckCircle, color: 'text-green-500', label: 'Available' };
    }
    return { icon: Clock, color: 'text-orange-500', label: 'Insufficient Credits' };
  };

  if (isLoading || !modelsData) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Loading AI Models...</DialogTitle>
          </DialogHeader>
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  const availableModels = filterAndSortModels(modelsData);
  const groupedModels = groupModelsByProvider(availableModels);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bot className="w-5 h-5" />
            Select AI Model
            {failedModel && (
              <Badge variant="destructive" className="ml-2">
                Fallback Required
              </Badge>
            )}
          </DialogTitle>
          <DialogDescription>
            Choose an AI model for your {taskType.replace('_', ' ')} task.
            {failedModel && (
              <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-700 font-medium">
                  The selected model ({failedModel}) encountered an error:
                </p>
                <p className="text-sm text-red-600 mt-1">{errorMessage}</p>
                <p className="text-sm text-red-700 mt-2">
                  Please select an alternative model below.
                </p>
              </div>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* User Info */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-blue-800">Your Plan: {userPlan}</p>
                <p className="text-sm text-blue-600">Available Credits: {creditBalance}</p>
              </div>
              <div className="text-right">
                <p className="text-sm font-medium text-blue-800">Base Cost: {baseCost} credits</p>
                <p className="text-xs text-blue-600">Final cost depends on selected model</p>
              </div>
            </div>
          </div>

          {/* Model Groups */}
          {Object.entries(groupedModels).map(([provider, models]) => {
            const ProviderIcon = PROVIDER_ICONS[provider] || Bot;
            const providerColor = PROVIDER_COLORS[provider] || 'bg-gray-100 text-gray-800';

            return (
              <div key={provider} className="space-y-3">
                <div className="flex items-center gap-2">
                  <ProviderIcon className="w-5 h-5" />
                  <h3 className="text-lg font-semibold">{provider}</h3>
                  <Badge className={cn('text-xs', providerColor)}>
                    {models.length} model{models.length !== 1 ? 's' : ''}
                  </Badge>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {models.map((model) => {
                    const totalCost = Math.ceil(baseCost * model.credit_multiplier);
                    const status = getModelStatus(model);
                    const StatusIcon = status.icon;
                    const isSelected = selectedModel?.id === model.id;
                    const isDisabled = !canAfford(model) || model.api_name === failedModel;

                    return (
                      <Card
                        key={model.id}
                        className={cn(
                          'cursor-pointer transition-all duration-200 hover:shadow-md',
                          isSelected && 'ring-2 ring-blue-500 bg-blue-50',
                          isDisabled && 'opacity-60 cursor-not-allowed'
                        )}
                        onClick={() => !isDisabled && setSelectedModel(model)}
                      >
                        <CardHeader className="pb-3">
                          <div className="flex items-center justify-between">
                            <CardTitle className="text-sm font-medium">
                              {model.display_name || model.model_name}
                            </CardTitle>
                            <div className="flex items-center gap-1">
                              <StatusIcon className={cn('w-4 h-4', status.color)} />
                            </div>
                          </div>
                          <CardDescription className="text-xs">
                            {model.context_window} context • {model.capability_index}/10 capability
                          </CardDescription>
                        </CardHeader>
                        <CardContent className="pt-0">
                          <div className="space-y-2">
                            <div className="flex items-center justify-between">
                              <span className="text-sm font-medium">Cost:</span>
                              <span className={cn(
                                'text-sm font-bold',
                                totalCost > baseCost ? 'text-orange-600' : 'text-green-600'
                              )}>
                                {totalCost} credits
                              </span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-sm">Multiplier:</span>
                              <Badge variant="outline" className="text-xs">
                                {model.credit_multiplier}x
                              </Badge>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-sm">Min Plan:</span>
                              <Badge variant="secondary" className="text-xs">
                                {model.min_plan_required}
                              </Badge>
                            </div>
                            {model.key_notes && (
                              <p className="text-xs text-gray-600 mt-2 italic">
                                {model.key_notes}
                              </p>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>
            );
          })}

          <Separator />

          {/* Action Buttons */}
          <div className="flex items-center justify-between">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <div className="flex items-center gap-3">
              {selectedModel && (
                <div className="text-sm text-gray-600">
                  Selected: {selectedModel.display_name || selectedModel.model_name} 
                  ({Math.ceil(baseCost * selectedModel.credit_multiplier)} credits)
                </div>
              )}
              <Button
                onClick={handleSelectModel}
                disabled={!selectedModel}
                className="bg-blue-600 hover:bg-blue-700"
              >
                Use Selected Model
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}